import React, { useState } from 'react';
import './UserEditModal.scss';

const UserEditModal = ({ user, onClose, onUpdate, currentUserRole }) => {
  const [formData, setFormData] = useState({
    username: user.username,
    role: user.role
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Define available roles based on current user's role
  const getAvailableRoles = () => {
    const allRoles = [
      { value: 'owner', label: 'Owner', description: 'Highest level access' },
      { value: 'admin', label: 'Admin', description: 'Full administrative access' },
      { value: 'moderator', label: 'Moderator', description: 'Can moderate content and users' },
      { value: 'user', label: 'User', description: 'Regular user with basic access' }
    ];

    // Only owners can assign owner role
    if (currentUserRole !== 'owner') {
      return allRoles.filter(role => role.value !== 'owner');
    }

    return allRoles;
  };

  const availableRoles = getAvailableRoles();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(null);
  };

  const validateForm = () => {
    if (!formData.username.trim()) {
      setError('Username is required');
      return false;
    }

    if (formData.username.length < 3) {
      setError('Username must be at least 3 characters long');
      return false;
    }

    if (formData.username.length > 20) {
      setError('Username must be 20 characters or less');
      return false;
    }

    if (!/^[a-zA-Z0-9]+$/.test(formData.username)) {
      setError('Username can only contain letters and numbers');
      return false;
    }

    if (!formData.role) {
      setError('Role is required');
      return false;
    }

    // Additional role validation
    if (formData.role === 'owner' && currentUserRole !== 'owner') {
      setError('Only owners can assign owner role');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await onUpdate(user.id, formData);
      
      if (result.success) {
        onClose();
      } else {
        setError(result.error || 'Failed to update user');
      }
    } catch (error) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const isFormChanged = () => {
    return formData.username !== user.username || formData.role !== user.role;
  };


  return (
    <div className="user-edit-modal__overlay" onClick={handleOverlayClick}>
      <div className="user-edit-modal__container">
        <div className="user-edit-modal__header">
          <h2>Edit User</h2>
          <button 
            onClick={onClose}
            className="user-edit-modal__close-button"
            type="button"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="user-edit-modal__form">
          {/* User Info Display */}
          <div className="user-edit-modal__user-info">
            <table className="user-edit-modal__info-table">
              <tbody>
                <tr>
                  <td className="user-edit-modal__info-label">User ID:</td>
                  <td className="user-edit-modal__info-value user-edit-modal__user-id">{user.id}</td>
                </tr>
                <tr>
                  <td className="user-edit-modal__info-label">Email:</td>
                  <td className="user-edit-modal__info-value">{user.email}</td>
                </tr>
                <tr>
                  <td className="user-edit-modal__info-label">Created:</td>
                  <td className="user-edit-modal__info-value">{new Date(user.created_at).toLocaleDateString()}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Editable Fields */}
          <div className="user-edit-modal__form-fields">
            <div className="user-edit-modal__form-group">
              <label htmlFor="username" className="user-edit-modal__label">
                Username
              </label>
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                className="user-edit-modal__input"
                disabled={loading}
                maxLength={20}
                required
              />
              <div className="user-edit-modal__field-info">
                3-20 characters, letters and numbers only
              </div>
            </div>

            <div className="user-edit-modal__form-group">
              <label htmlFor="role" className="user-edit-modal__label">
                Role
              </label>
              <select
                id="role"
                name="role"
                value={formData.role}
                onChange={handleInputChange}
                className="user-edit-modal__select"
                disabled={loading}
                required
              >
                {availableRoles.map(role => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </select>
              
              {/* Role Description */}
              {formData.role && (
                <div className="user-edit-modal__role-description">
                  {availableRoles.find(r => r.value === formData.role)?.description}
                </div>
              )}
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="user-edit-modal__error">
              {error}
            </div>
          )}


          {/* Self-Edit Warning */}
          {user.id === currentUserRole && formData.role !== user.role && !['admin', 'owner'].includes(formData.role) && (
            <div className="user-edit-modal__danger-warning">
              🚨 Warning: You are about to demote yourself. This will remove your admin access!
            </div>
          )}

          {/* Form Actions */}
          <div className="user-edit-modal__actions">
            <button
              type="button"
              onClick={onClose}
              className="user-edit-modal__button user-edit-modal__button--cancel"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="user-edit-modal__button user-edit-modal__button--save"
              disabled={loading || !isFormChanged()}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserEditModal;